import { z } from 'zod';
import { BARK_TEXT_MAX, BarkTarget } from './bark.js';
import { PrivateEntityId } from './encrypted-entities.js';
/**
 * Phase 3 triage toolset (docs/ux/project-tab-spec.md §2.4, `triage-tools` + `segmentation`).
 * Additive: spread into `VIKO_MCP_TOOLS` from mcp.ts. Every id is re-scoped to the calling run's
 * project by the daemon; tools other than `viko_task_find` are triage-only.
 */
const Id = PrivateEntityId;
const RunId = z.string().min(1).max(256);
const LocalId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
// --- viko_task_find (triage, work) -----------------------------------------------------------------
export const VikoTaskFindInput = z.object({
    /** The bark or a paraphrase of the problem; matched against open tasks and lanes. */
    query: z.string().min(1).max(BARK_TEXT_MAX),
    limit: z.number().int().min(1).max(20).optional(),
    /** Also match landed/cancelled tasks (default: open work only). */
    includeClosed: z.boolean().optional(),
});
export const TaskFindCandidate = z.object({
    kind: z.enum(['task', 'workstream']),
    id: Id,
    title: z.string(),
    status: z.string(),
    workstreamId: Id.optional(),
    score: z.number().min(0).max(1),
    /** `duplicate`: very likely the same problem (merge); `overlap`: related (link or extend). */
    relation: z.enum(['duplicate', 'overlap']),
    shared: z.array(z.string()).max(20),
});
export const VikoTaskFindOutput = z.object({ candidates: z.array(TaskFindCandidate).max(20) });
// --- viko_bark_link (triage) ---------------------------------------------------------------------
export const VikoBarkLinkInput = z.object({
    barkId: Id,
    /** The existing work the bark belongs to; nothing about it changes. */
    target: z.object({
        type: z.enum(['task', 'run', 'workstream', 'decision']),
        id: z.string().min(1).max(256),
    }),
    note: z.string().max(500).optional(),
});
export const VikoBarkLinkOutput = z.object({ ok: z.boolean(), target: BarkTarget });
// --- viko_workstream_create / viko_workstream_update (triage) -------------------------------------
export const VikoWorkstreamCreateInput = z.object({
    name: z.string().trim().min(1).max(120),
    brief: z.string().max(8000).optional(),
    /** Lane order (fractional index); default after the last lane. */
    rank: z.number().finite().optional(),
    agent: z.enum(['claude', 'codex']).optional(),
    /** The bark that needed a new lane (provenance + undo). */
    barkId: Id.optional(),
    rationale: z.string().max(2000).optional(),
});
export const VikoWorkstreamCreateOutput = z.object({
    workstreamId: Id,
    /** False when a live lane with the same name already existed (it is returned instead). */
    created: z.boolean(),
});
export const VikoWorkstreamUpdateInput = z.object({
    workstreamId: Id,
    name: z.string().trim().min(1).max(120).optional(),
    brief: z.string().max(8000).optional(),
    appendBrief: z.string().max(4000).optional(),
    rank: z.number().finite().optional(),
    paused: z.boolean().optional(),
    barkId: Id.optional(),
});
export const VikoWorkstreamUpdateOutput = z.object({ ok: z.boolean() });
// --- viko_capture_attach (triage) ----------------------------------------------------------------
export const VikoCaptureAttachInput = z.object({
    barkId: Id,
    target: z.object({ type: z.enum(['task', 'run']), id: z.string().min(1).max(256) }),
    /** Subset of the bark's attachment artifact ids; default all of them. */
    artifactIds: z.array(z.string().min(1).max(256)).max(20).optional(),
});
export const VikoCaptureAttachOutput = z.object({
    attached: z.number().int().nonnegative(),
    /** Tasks keep the refs; a live run receives them as a message; `none`: nothing to attach. */
    status: z.enum(['attached', 'delivered', 'not_live', 'none']),
});
// --- viko_bark_undo_plan (triage) ----------------------------------------------------------------
export const VikoBarkUndoPlanInput = z.object({
    barkId: Id,
    /** One action branch; default every active action of the item. */
    actionId: LocalId.optional(),
    /** Apply the compensating steps now (redirect / re-triage), recording the old branch as undone. */
    apply: z.boolean().optional(),
    reason: z.enum(['undo', 'redirect', 'retriage']).optional(),
});
export const UndoStep = z.object({
    op: z.enum([
        'cancel-task',
        'restore-task',
        'correct-run',
        'stop-run',
        'archive-workstream',
        'withdraw-decision',
    ]),
    target: BarkTarget,
    effect: z.string().max(500),
    /** False when the step cannot be undone cleanly (a run already started, work landed…). */
    possible: z.boolean(),
});
export const VikoBarkUndoPlanOutput = z.object({
    steps: z.array(UndoStep).max(200),
    applied: z.boolean(),
    /** Why a clean undo is not possible (then prefer a new outcome that builds on the old one). */
    blocked: z.string().max(500).optional(),
});
// --- viko_steer_scope_check (triage; segmentation) -----------------------------------------------
export const VikoSteerScopeCheckInput = z.object({
    runId: RunId,
    /** The message to check (the bark text, or a direct steer's words). */
    text: z.string().min(1).max(BARK_TEXT_MAX),
    barkId: Id.optional(),
});
export const VikoSteerScopeCheckOutput = z.object({
    verdict: z.enum(['in_scope', 'out_of_scope', 'unclear']),
    reason: z.string().max(500),
    /** What the run is working on (so the agent can judge `unclear` itself). */
    run: z.object({
        task: z.string().optional(),
        lane: z.string().optional(),
        brief: z.string().max(1000).optional(),
    }),
    /** Other open work the message matches better (target mismatch). */
    matches: z
        .array(z.object({ type: z.enum(['task', 'workstream']), id: Id, title: z.string() }))
        .max(5),
});
export const TRIAGE_P3_MCP_TOOLS = {
    viko_task_find: { input: VikoTaskFindInput, output: VikoTaskFindOutput },
    viko_bark_link: { input: VikoBarkLinkInput, output: VikoBarkLinkOutput },
    viko_workstream_create: { input: VikoWorkstreamCreateInput, output: VikoWorkstreamCreateOutput },
    viko_workstream_update: { input: VikoWorkstreamUpdateInput, output: VikoWorkstreamUpdateOutput },
    viko_capture_attach: { input: VikoCaptureAttachInput, output: VikoCaptureAttachOutput },
    viko_bark_undo_plan: { input: VikoBarkUndoPlanInput, output: VikoBarkUndoPlanOutput },
    viko_steer_scope_check: { input: VikoSteerScopeCheckInput, output: VikoSteerScopeCheckOutput },
};
/** P3 tools only a `role: 'triage'` run may list or call (`viko_task_find` is for work runs too). */
export const TRIAGE_P3_ONLY_TOOLS = Object.keys(TRIAGE_P3_MCP_TOOLS).filter((t) => t !== 'viko_task_find');
// --- viko_context sections (triage gets all; work runs opt in) ------------------------------------
const Short = (n) => z.string().max(n);
export const ContextLane = z.object({
    id: Id,
    name: z.string(),
    status: z.string(),
    rank: z.number().optional(),
    paused: z.boolean(),
    brief: Short(600).optional(),
    liveRun: z.object({ runId: RunId, status: z.string() }).optional(),
    tasks: z
        .array(z.object({
        id: Id,
        title: z.string(),
        status: z.string(),
        priority: z.number(),
        rank: z.number().optional(),
        runId: RunId.optional(),
    }))
        .max(100),
});
export const ContextBark = z.object({
    id: Id,
    title: z.string().optional(),
    /** First 300 characters of the verbatim text. */
    excerpt: Short(300),
    status: z.string(),
    mode: z.enum(['quick', 'discuss']),
    at: z.number(),
    decisions: z.array(z.string()).max(50),
    targets: z.array(BarkTarget).max(50),
});
export const ContextAsk = z.object({
    questionId: z.string(),
    runId: RunId,
    question: Short(1000),
    blocking: z.boolean(),
    at: z.number(),
});
export const ContextLanding = z.object({
    taskId: Id.optional(),
    workstreamId: Id.optional(),
    title: z.string(),
    state: z.enum(['review', 'landing', 'queued', 'landed']),
    runId: RunId.optional(),
    landedAt: z.number().optional(),
});
export const ContextGoal = z.object({ text: Short(4000) });
//# sourceMappingURL=triage-tools.js.map